import { LogEvent } from './logger.types';

export interface BatcherOptions {
  flushIntervalMs: number;
  maxBatchSize: number;
  maxQueueSize: number;
  onFlush: (events: LogEvent[]) => void | Promise<void>;
}

export interface Batcher {
  push(event: LogEvent): void;
  flushNow(): Promise<void>;
  drain(): LogEvent[];
  stop(): void;
}

export function createBatcher(opts: BatcherOptions): Batcher {
  const queue: LogEvent[] = [];
  let timer: ReturnType<typeof setInterval> | null = null;
  let warnedOverflow = false;
  let stopped = false;

  function schedule(): void {
    if (timer || stopped) return;
    timer = setInterval(() => { void flush(); }, opts.flushIntervalMs);
  }

  async function flush(): Promise<void> {
    if (queue.length === 0) return;
    const drained = queue.splice(0, queue.length);
    try { await opts.onFlush(drained); } catch { /* swallow */ }
  }

  schedule();

  return {
    push(event) {
      if (stopped) return;
      if (queue.length >= opts.maxQueueSize) {
        queue.shift();
        if (!warnedOverflow) {
          warnedOverflow = true;
          console.warn('[logger] event queue overflow; dropping oldest events');
        }
      }
      queue.push(event);
      if (queue.length >= opts.maxBatchSize) void flush();
    },
    flushNow() { return flush(); },
    drain() { return queue.splice(0, queue.length); },
    stop() {
      stopped = true;
      if (timer) { clearInterval(timer); timer = null; }
    },
  };
}
